import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Stack, Chip, Alert, Button,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Assessment as ReviewIcon,
  Schedule as PendingIcon,
  CheckCircle as DoneIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { reviewsApi, type ReviewAssignment } from '@/api/endpoints/performance';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS, MODULE_COLORS } from '@/theme/colorTokens';
import { usePaletteAccents } from '@/hooks/usePaletteAccents';
import PageHelpIcon from '@/components/PageHelpIcon';

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
};

const reviewerTypeLabels: Record<string, string> = {
  self: 'Self',
  manager: 'Manager',
  peer: 'Peer',
  direct_report: 'Direct Report',
  external: 'External',
};

function KPICard({ label, value, icon, color = 'primary.main' }: {
  label: string; value: string | number; icon?: React.ReactNode; color?: string;
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: '16px 20px' }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: '12px',
          bgcolor: `${color}14`, color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box>
          <Typography sx={{ fontSize: TYPE['3xl'], fontWeight: TYPE.bold }}>{value}</Typography>
          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium, color: 'text.secondary' }}>{label}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

export default function MyReviewsPage({ embedded }: { embedded?: boolean } = {}) {
  const ACCENT = usePaletteAccents();
  const statusColors: Record<string, string> = {
    pending: STATUS_COLORS.warning,
    in_progress: ACCENT.blue,
    submitted: STATUS_COLORS.success,
    acknowledged: MODULE_COLORS.hr,
  };
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'pending' | 'submitted'>('all');

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-reviews'],
    queryFn: async () => {
      const res = await reviewsApi.myReviews();
      return res.data;
    },
  });

  const reviews = data?.items ?? [];

  const totalCount = reviews.length;
  const pendingCount = reviews.filter(r => r.status === 'pending' || r.status === 'in_progress').length;
  const submittedCount = reviews.filter(r => r.status === 'submitted' || r.status === 'acknowledged').length;

  const filtered = filter === 'all'
    ? reviews
    : filter === 'pending'
      ? reviews.filter(r => r.status === 'pending' || r.status === 'in_progress')
      : reviews.filter(r => r.status === 'submitted' || r.status === 'acknowledged');

  const isActionable = (status: string) => status === 'pending' || status === 'in_progress';

  return (
    <Box>
      {/* Header */}
      {!embedded && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontSize: TYPE['5xl'], fontWeight: TYPE.bold }}>
              My Reviews
            </Typography>
            <PageHelpIcon pageKey="performance-my-reviews" />
          </Stack>
          <Typography sx={{ fontSize: TYPE.md, color: 'text.secondary', mb: 2 }}>
            Review assignments where you are the reviewer
          </Typography>
        </>
      )}

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <KPICard
            label="Total Assignments"
            value={isLoading ? '-' : totalCount}
            icon={<ReviewIcon />}
            color={ACCENT.blue}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <KPICard
            label="Pending"
            value={isLoading ? '-' : pendingCount}
            icon={<PendingIcon />}
            color={STATUS_COLORS.warning}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <KPICard
            label="Submitted"
            value={isLoading ? '-' : submittedCount}
            icon={<DoneIcon />}
            color={STATUS_COLORS.success}
          />
        </Grid>
      </Grid>

      {/* Filter Chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
        {(['all', 'pending', 'submitted'] as const).map(f => (
          <Chip
            key={f}
            label={f === 'all' ? `All (${totalCount})` : f === 'pending' ? `Pending (${pendingCount})` : `Submitted (${submittedCount})`}
            onClick={() => setFilter(f)}
            variant={filter === f ? 'filled' : 'outlined'}
            color={filter === f ? 'primary' : 'default'}
            sx={{ fontWeight: filter === f ? TYPE.semibold : TYPE.normal, fontSize: TYPE.sm }}
          />
        ))}
      </Stack>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load review assignments. Please try again.
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Grid container spacing={2}>
          {[1, 2, 3].map(i => (
            <Grid key={i} size={{ xs: 12, md: 6, lg: 4 }}>
              <Card sx={{ height: 180 }}>
                <CardContent>
                  <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, height: '100%', minHeight: 140 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Empty State */}
      {!isLoading && !error && filtered.length === 0 && (
        <Box sx={{
          textAlign: 'center', py: 8, px: 3,
          bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed',
          borderColor: 'divider',
        }}>
          <ReviewIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.semibold, color: 'text.secondary', mb: 0.5 }}>
            All caught up! No pending reviews.
          </Typography>
          <Typography sx={{ fontSize: TYPE.md, color: 'text.disabled' }}>
            {filter !== 'all'
              ? 'Try changing your filter to see other assignments.'
              : 'Review assignments will appear here when a cycle is launched.'}
          </Typography>
        </Box>
      )}

      {/* Review Cards */}
      {!isLoading && filtered.length > 0 && (
        <Grid container spacing={2}>
          {filtered.map((assignment) => {
            const actionable = isActionable(assignment.status);
            const isDue = assignment.due_date && new Date(assignment.due_date) < new Date() && actionable;

            return (
              <Grid key={assignment.id} size={{ xs: 12, md: 6, lg: 4 }}>
                <Card sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: isDue ? 'warning.main' : 'divider',
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                  '&:hover': { boxShadow: 4, borderColor: actionable ? 'primary.main' : 'divider' },
                }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.5, p: '20px' }}>
                    {/* Employee Name */}
                    <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.semibold, lineHeight: 1.3 }}>
                      {assignment.employee_name ?? 'Unknown Employee'}
                    </Typography>

                    {/* Cycle Name */}
                    <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                      {(assignment as ReviewAssignment & { cycle_name?: string }).cycle_name ?? `Cycle`}
                    </Typography>

                    {/* Chips */}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        label={reviewerTypeLabels[assignment.reviewer_type] ?? assignment.reviewer_type}
                        size="small"
                        sx={{
                          fontSize: TYPE.xs, fontWeight: TYPE.medium,
                          bgcolor: 'action.selected', color: 'text.primary',
                        }}
                      />
                      <Chip
                        label={statusLabels[assignment.status] ?? assignment.status}
                        size="small"
                        sx={{
                          fontSize: TYPE.xs, fontWeight: TYPE.semibold,
                          bgcolor: `${statusColors[assignment.status] ?? STATUS_COLORS.default}18`,
                          color: statusColors[assignment.status] ?? STATUS_COLORS.default,
                        }}
                      />
                      {assignment.is_anonymous && (
                        <Chip
                          label="Anonymous"
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: TYPE.xs, fontWeight: TYPE.medium }}
                        />
                      )}
                    </Stack>

                    {/* Due Date */}
                    <Typography sx={{
                      fontSize: TYPE.sm, color: isDue ? 'warning.main' : 'text.secondary',
                      fontWeight: isDue ? TYPE.semibold : TYPE.normal,
                    }}>
                      {isDue ? 'Overdue' : 'Due'}: {formatDate(assignment.due_date)}
                    </Typography>

                    {/* Spacer */}
                    <Box sx={{ flex: 1 }} />

                    {/* Action */}
                    {actionable ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/performance/review/${assignment.id}`)}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.sm }}
                      >
                        {assignment.status === 'in_progress' ? 'Continue Review' : 'Start Review'}
                      </Button>
                    ) : (
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 'auto' }}>
                        <DoneIcon sx={{ fontSize: 18, color: 'success.main' }} />
                        <Typography sx={{ fontSize: TYPE.sm, color: 'success.main', fontWeight: TYPE.medium }}>
                          Submitted {formatDate(assignment.submitted_at)}
                        </Typography>
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
